import { useEffect, useRef, type RefObject } from "react";

/**
 * Custom hook to detect clicks outside of a specified element
 * Commonly used for dropdowns, modals, tooltips, context menus, etc.
 *
 * @param handler - Function to call when click outside is detected
 * @param enabled - Whether the hook should be active (default: true)
 * @returns ref - Ref to attach to the element that should detect outside clicks
 *
 * @example
 * ```tsx
 * const MyDropdown = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false));
 *
 *   return (
 *     <div ref={dropdownRef}>
 *       {isOpen && <div>Dropdown content</div>}
 *     </div>
 *   );
 * };
 * ```
 */
export function useClickOutside<T extends HTMLElement = HTMLDivElement>(
  handler: (event: MouseEvent | TouchEvent) => void,
  enabled: boolean = true
): RefObject<T | null> {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler(event);
      }
    };

    // Use mousedown and touchstart for better UX
    // mousedown fires before onClick, preventing issues with buttons
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [handler, enabled]);

  return ref;
}

/**
 * Hook variant that accepts multiple refs for more complex scenarios
 * Useful when you need to exclude multiple elements from click outside detection
 *
 * @param handler - Function to call when click outside is detected
 * @param refs - Array of refs to check against
 * @param enabled - Whether the hook should be active (default: true)
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const dropdownRef = useRef<HTMLDivElement>(null);
 *   const triggerRef = useRef<HTMLButtonElement>(null);
 *
 *   useClickOutsideMultiple(
 *     () => setIsOpen(false),
 *     [dropdownRef, triggerRef]
 *   );
 *
 *   return (
 *     <>
 *       <button ref={triggerRef}>Open</button>
 *       {isOpen && <div ref={dropdownRef}>Content</div>}
 *     </>
 *   );
 * };
 * ```
 */
export function useClickOutsideMultiple(
  handler: (event: MouseEvent | TouchEvent) => void,
  refs: RefObject<HTMLElement>[],
  enabled: boolean = true
): void {
  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      // Check if click is outside all specified elements
      const isOutside = refs.every(
        (ref) => !ref.current || !ref.current.contains(target)
      );

      if (isOutside) {
        handler(event);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [handler, refs, enabled]);
}
